import React, { useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

import {
  useRequestParentTakeoverCode, useVerifyParentTakeover,
} from '../../../lib/services/parent.service';
import {
  IParentAccountConflict, IParentMaskedCredential, IParentVerifyResult,
} from '../../../lib/types';
import { TwoStepOtpForm } from '../../_core/TwoStepOtpForm/two-step-otp-form.component';
import { useTwoStepOtp } from '../../_core/TwoStepOtpForm/use-two-step-otp';
import { otpVerifyValidationSchema } from '../../_core/TwoStepOtpForm/two-step-otp-form.validation';
import { PrimaryButton, SecondaryButton } from '../../_core/_ui/buttons.component';

interface IParentAccountSwitchDialogProps {
  conflict: IParentAccountConflict | null;
  credential: IParentMaskedCredential | null;
  onClose: () => void;
  onSwitched: (result: IParentVerifyResult) => void;
}

const COPY = {
  requestHeading: 'Move these players to this account',
  requestDescription: '',
  verifySubmitLabel: 'Verify & move players',
  retryLabel: 'Try again',
  cancelLabel: 'Cancel',
};

export const ParentAccountSwitchDialog: React.FC<IParentAccountSwitchDialogProps> = ({
  conflict,
  credential,
  onClose,
  onSwitched,
}) => {
  const [requestTakeoverCode] = useRequestParentTakeoverCode();
  const [verifyTakeover] = useVerifyParentTakeover();

  // The masked string stands in for the destination throughout: it is what the
  // verify step displays and what keys the resend cooldown. The real address is
  // resolved server-side from the credential key, which is why it is safe here.
  const otp = useTwoStepOtp({
    onRequest: async () => {
      if (!conflict || !credential) return;
      await requestTakeoverCode({ variables: { _id: conflict._id, credentialKey: credential.key } });
    },
    onVerify: async (_destination, code) => {
      if (!conflict || !credential) return;
      const response = await verifyTakeover({
        variables: { _id: conflict._id, credentialKey: credential.key, code },
      });
      const result: IParentVerifyResult | undefined = response.data?.parentVerifyTakeover;
      if (!result) throw new Error('That code could not be confirmed. Please try again.');
      onSwitched(result);
    },
  });

  // Step 1 has no address to type — the code goes to an address the parent can
  // only see masked — so send it on open and land straight on the code entry.
  const requestedFor = useRef<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const key = conflict && credential ? `${conflict._id}:${credential.key}` : null;
  // Keyed by attempt as well, so Try again re-arms the send-once guard for one more run.
  useEffect(() => {
    const token = key && `${key}:${attempt}`;
    if (!token || requestedFor.current === token) return;
    requestedFor.current = token;
    otp.handleRequest(
      { identifier: credential!.masked },
      { setSubmitting: () => undefined } as any,
    );
  }, [key, attempt, credential, otp]);

  useEffect(() => {
    if (!key) requestedFor.current = null;
  }, [key]);

  if (!conflict || !credential) return null;

  // Not memoized: a cheap boolean over two primitives, not fed to any hook dep array.
  const sendFailed = otp.step !== 'verify' && Boolean(otp.submitError);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogContent>
        {otp.step === 'verify' ? (
          <Formik
            key="verify"
            initialValues={{ code: '' }}
            validationSchema={otpVerifyValidationSchema}
            onSubmit={otp.handleVerify}
          >
            {(form) => (
              <TwoStepOtpForm
                step="verify"
                form={form}
                destination={credential.masked}
                submitError={otp.submitError}
                onBack={onClose}
                copy={COPY}
                resendSecondsRemaining={otp.resendSecondsRemaining}
                resending={otp.resending}
                onResend={otp.handleResend}
              />
            )}
          </Formik>
        ) : sendFailed ? (
          <Box py={4} display="flex" flexDirection="column" alignItems="center">
            <Typography variant="body2" color="error">{otp.submitError}</Typography>
            <Box mt={2} display="flex">
              <Box mr={1}>
                <PrimaryButton onClick={() => setAttempt((a) => a + 1)}>{COPY.retryLabel}</PrimaryButton>
              </Box>
              <SecondaryButton onClick={onClose}>{COPY.cancelLabel}</SecondaryButton>
            </Box>
          </Box>
        ) : (
          <Box py={4} display="flex" flexDirection="column" alignItems="center">
            <CircularProgress size={28} />
            <Box mt={2}>
              <Typography variant="body2">{`Sending a code to ${credential.masked}…`}</Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
